/**
 * Central de avisos — a aba que controla TUDO que o sistema notifica
 * (13/08, pedido do Lucas).
 *
 * Quatro blocos, na ordem em que a pergunta aparece:
 *   1. O expediente — se o sistema pode avisar AGORA, e por quê.
 *   2. Cada aviso: o que faz, quem recebe, ligado ou não, de quanto em quanto.
 *   3. Quem tem o celular ligado para receber (e o teste).
 *   4. O histórico: o que saiu de verdade, para quem, e se chegou.
 */

import { api, ErroApi } from '../../api.js';
import { escapar, dataHora } from '../../comum/formato.js';
import { avisar, avisarBloqueante } from '../../comum/aviso.js';
import { ativarPush, diagnosticoDoAparelho, type DiagnosticoPush } from '../../comum/push.js';

interface AvisoCentral {
  chave: string;
  nome: string;
  quem: string;
  oQueFaz: string;
  agendado: boolean;
  ativo: boolean;
  cadaMin: number | null;
}

interface Central {
  avisos: AvisoCentral[];
  expediente: {
    dentro: boolean;
    motivo: string;
    agora: string;
    linhas: string[];
    feriados: string[];
  };
  pedidosEsperando: number;
  inscritos: { nome: string; modulos: string[]; aparelhos: number }[];
  historico: {
    chave: string; nome: string; titulo: string; corpo: string;
    entregues: number; enviado_em: string; pessoa: string | null;
  }[];
}

const MOTIVO: Record<string, string> = {
  expediente: 'dentro do expediente — os avisos estão saindo normalmente',
  almoco: 'horário de almoço (11:00–12:00) — os avisos agendados esperam',
  fora_do_horario: 'fora do horário de trabalho — os avisos agendados esperam',
  fim_de_semana: 'fim de semana — os avisos agendados esperam',
  feriado: 'feriado — os avisos agendados esperam',
};

export function montarAvisos(raiz: HTMLElement): void {
  raiz.innerHTML = `<p class="carregando">Carregando a central de avisos…</p>`;

  async function carregar(): Promise<void> {
    try {
      desenhar(await api.get<Central>('/api/avisos/central'));
    } catch (erro) {
      raiz.innerHTML = `<p class="aviso">${escapar(
        erro instanceof ErroApi ? erro.message : 'Não consegui carregar a central.',
      )}</p>`;
    }
  }

  function desenhar(c: Central): void {
    const dataBr = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
    const hoje = new Date().toISOString().slice(0, 10);
    const proximos = c.expediente.feriados.filter((f) => f >= hoje).slice(0, 4);

    raiz.innerHTML = `
      <section class="ped-painel">
        <h3>🕗 Expediente</h3>
        <p class="av-estado ${c.expediente.dentro ? 'dentro' : 'fora'}">
          <b>${c.expediente.agora}</b> — ${escapar(MOTIVO[c.expediente.motivo] ?? c.expediente.motivo)}
        </p>
        <ul class="av-lista">${c.expediente.linhas.map((l) => `<li>${escapar(l)}</li>`).join('')}</ul>
        <p class="dica">Próximos feriados sem aviso: ${
          proximos.length ? proximos.map(dataBr).join(' · ') : 'nenhum até o fim do ano'
        }</p>
        <p class="dica">
          Só os avisos <b>agendados</b> respeitam o expediente. O que é resposta a uma ação
          (pedido enviado, romaneio separado, entrega feita) sai na hora, a qualquer momento —
          é a pessoa que acabou de agir esperando o retorno.
        </p>
      </section>

      <section class="ped-painel">
        <h3>🔔 Avisos do sistema <span class="sub-inline">${c.avisos.filter((a) => a.ativo).length} de ${c.avisos.length} ligados</span></h3>
        ${
          c.pedidosEsperando > 0
            ? `<p class="dica">Agora há <b>${c.pedidosEsperando}</b> pedido(s) esperando o CD — o lembrete volta a cada intervalo abaixo.</p>`
            : ''
        }
        <div class="rolagem"><table class="tabela"><thead><tr>
          <th>Aviso</th><th>Quem recebe</th><th>Quando</th><th class="num">Repete</th><th>Ligado</th>
        </tr></thead><tbody>
          ${c.avisos
            .map(
              (a) => `<tr>
                <td><b>${escapar(a.nome)}</b><span class="sub">${escapar(a.oQueFaz)}</span></td>
                <td>${escapar(a.quem)}</td>
                <td>${a.agendado ? '⏱️ agendado <span class="sub">(só no expediente)</span>' : '⚡ na hora do fato'}</td>
                <td class="num">${
                  a.agendado
                    ? `<input class="av-min" type="number" min="15" max="1440" step="15"
                             value="${a.cadaMin ?? 60}" data-min="${escapar(a.chave)}"> min`
                    : '—'
                }</td>
                <td><label class="av-chave">
                  <input type="checkbox" data-liga="${escapar(a.chave)}" ${a.ativo ? 'checked' : ''}>
                  <span>${a.ativo ? 'ligado' : 'desligado'}</span>
                </label></td>
              </tr>`,
            )
            .join('')}
        </tbody></table></div>
        <div class="conf-acoes">
          <button class="btn btn-claro" id="av-teste">📲 Mandar um teste para mim</button>
          <button class="btn btn-claro" id="av-lembrar">⏱️ Rodar o lembrete agora</button>
        </div>
      </section>

      <section class="ped-painel" id="av-aparelho">
        <h3>🔎 Este aparelho</h3>
        <p class="carregando">Conferindo…</p>
      </section>

      <section class="ped-painel" id="av-whatsapp">
        <h3>💬 WhatsApp <span class="sub-inline">teste via Zernio</span></h3>
        <p class="carregando">Consultando…</p>
      </section>

      <section class="ped-painel">
        <h3>📱 Quem recebe no celular <span class="sub-inline">${c.inscritos.length} pessoa(s)</span></h3>
        ${
          c.inscritos.length === 0
            ? `<p class="dica">Ninguém ativou os avisos ainda. Cada pessoa ativa no próprio módulo,
                 tocando em <b>🔔 Ativar avisos</b> — é o navegador que pede a permissão, e ninguém
                 pode fazer isso pelos outros.</p>`
            : `<div class="rolagem"><table class="tabela"><thead><tr>
                 <th>Pessoa</th><th>Módulos</th><th class="num">Aparelhos</th>
               </tr></thead><tbody>
               ${c.inscritos
                 .map(
                   (i) => `<tr><td>${escapar(i.nome)}</td>
                     <td>${escapar(i.modulos.join(', ') || '—')}</td>
                     <td class="num">${i.aparelhos}</td></tr>`,
                 )
                 .join('')}
               </tbody></table></div>`
        }
      </section>

      <section class="ped-painel">
        <h3>📜 O que o sistema avisou <span class="sub-inline">últimos ${c.historico.length}</span></h3>
        ${
          c.historico.length === 0
            ? `<p class="dica">Nada ainda.</p>`
            : `<div class="rolagem"><table class="tabela"><thead><tr>
                 <th>Quando</th><th>Aviso</th><th>Para</th><th>Mensagem</th><th class="num">Chegou</th>
               </tr></thead><tbody>
               ${c.historico
                 .map(
                   (h) => `<tr>
                     <td>${dataHora(h.enviado_em)}</td>
                     <td>${escapar(h.nome)}</td>
                     <td>${escapar(h.pessoa ?? '—')}</td>
                     <td>${escapar(h.titulo)}<span class="sub">${escapar(h.corpo)}</span></td>
                     <td class="num">${
                       h.entregues > 0
                         ? `${h.entregues} ✓`
                         : '<span class="av-nao" title="A pessoa não tem nenhum aparelho com os avisos ligados">0</span>'
                     }</td>
                   </tr>`,
                 )
                 .join('')}
               </tbody></table></div>`
        }
      </section>`;

    void desenharAparelho();
    void desenharWhatsApp();

    // Liga/desliga: salva no ato — configuração que precisa de "salvar" é
    // configuração que fica pela metade.
    raiz.querySelectorAll<HTMLInputElement>('[data-liga]').forEach((caixa) => {
      caixa.addEventListener('change', () => {
        void salvar(caixa.dataset['liga']!, { ativo: caixa.checked }, () => {
          caixa.nextElementSibling!.textContent = caixa.checked ? 'ligado' : 'desligado';
        });
      });
    });
    raiz.querySelectorAll<HTMLInputElement>('[data-min]').forEach((campo) => {
      campo.addEventListener('change', () => {
        const cadaMin = Number(campo.value);
        if (!Number.isFinite(cadaMin) || cadaMin < 15) {
          campo.value = '60';
          return avisarBloqueante('O intervalo mínimo é de 15 minutos.');
        }
        void salvar(campo.dataset['min']!, { cadaMin });
      });
    });

    raiz.querySelector('#av-teste')!.addEventListener('click', () => {
      void api
        .post<{ entregues: number }>('/api/avisos/teste', {})
        .then((r) =>
          avisar(
            r.entregues > 0
              ? `Teste enviado para ${r.entregues} aparelho(s).`
              : 'Você ainda não ativou os avisos neste navegador — toque em 🔔 no seu módulo.',
            r.entregues > 0 ? 'ok' : 'atencao',
          ),
        )
        .catch((e) => avisarBloqueante(e instanceof ErroApi ? e.message : 'Não consegui testar.'));
    });

    raiz.querySelector('#av-lembrar')!.addEventListener('click', () => {
      void api
        .post<{ enviados: number; pedidos: string[]; motivo: string }>('/api/avisos/lembrar-agora', {})
        .then((r) => {
          if (r.motivo === 'enviado') {
            avisar(`Lembrete enviado (${r.enviados} aparelho(s)) sobre: ${r.pedidos.join(', ')}.`);
          } else if (r.motivo === 'nada_pendente') {
            avisar('Nenhum pedido esperando — nada a lembrar.');
          } else if (r.motivo === 'desligado') {
            avisar('Este aviso está desligado aqui na central.', 'atencao');
          } else {
            avisar(`Fora do expediente (${MOTIVO[r.motivo] ?? r.motivo}) — o lembrete espera.`, 'atencao');
          }
          void carregar();
        })
        .catch((e) => avisarBloqueante(e instanceof ErroApi ? e.message : 'Não consegui rodar.'));
    });
  }

  /**
   * O DIAGNÓSTICO DO APARELHO — item por item, o que falta para o aviso
   * chegar aqui (14/08). Existe porque "não estou conseguindo fazer o push
   * funcionar" não tem conserto sem saber ONDE parou: quase sempre é iPhone
   * fora da Tela de Início ou permissão bloqueada, e nenhum dos dois é
   * defeito do sistema — mas os dois pareciam.
   */
  async function desenharAparelho(): Promise<void> {
    const caixa = raiz.querySelector<HTMLElement>('#av-aparelho');
    if (!caixa) return;
    const d: DiagnosticoPush = await diagnosticoDoAparelho();
    const sim = (v: boolean) => (v ? '<b class="av-ok">sim</b>' : '<b class="av-nao">não</b>');
    const permissao =
      { granted: '<b class="av-ok">permitida</b>', denied: '<b class="av-nao">bloqueada</b>', default: 'ainda não pedida' }[
        d.permissao as string
      ] ?? String(d.permissao);

    caixa.innerHTML = `
      <h3>🔎 Este aparelho ${d.inscrito ? '<span class="sub-inline av-ok">recebendo avisos ✓</span>' : ''}</h3>
      <ul class="av-lista">
        <li>Navegador com suporte a aviso: ${sim(d.suporta)}</li>
        <li>Permissão de notificação: ${permissao}</li>
        <li>Aberto como app instalado: ${sim(d.appInstalado)}${
          d.iphone ? ' <span class="sub-inline">(obrigatório no iPhone)</span>' : ''
        }</li>
        <li>App instalado no navegador (service worker): <b>${d.serviceWorker}</b></li>
        <li>Inscrito para receber aqui: ${sim(d.inscrito)}</li>
      </ul>
      ${d.impedimento ? `<p class="aviso">${escapar(d.impedimento)}</p>` : ''}
      <div class="conf-acoes">
        <button class="btn" id="av-ativar-aqui" ${d.inscrito ? 'disabled' : ''}>
          ${d.inscrito ? '✓ Já ativado neste aparelho' : '🔔 Ativar avisos neste aparelho'}
        </button>
      </div>`;

    caixa.querySelector('#av-ativar-aqui')?.addEventListener('click', () => {
      const b = caixa.querySelector<HTMLButtonElement>('#av-ativar-aqui')!;
      b.disabled = true;
      b.textContent = '⏳ Ativando…';
      void ativarPush().then((r) => {
        if (r.estado === 'ativado') {
          avisar('Avisos ativados neste aparelho — use o teste acima para confirmar.');
          void carregar();
        } else {
          // O motivo COMPLETO, com o erro técnico: é o que permite consertar
          // em vez de tentar de novo às cegas.
          avisarBloqueante(r.detalhe);
          void desenharAparelho();
        }
      });
    });
  }

  /**
   * 💬 WHATSAPP (21/08): enquanto o número próprio (62 3602-8654) está na
   * análise regulatória, o teste usa o número compartilhado do Zernio.
   * Fluxo: digitar o telefone → chega o template de ativação → RESPONDER a
   * mensagem → a sessão ativa → aí o teste de texto livre funciona.
   */
  async function desenharWhatsApp(): Promise<void> {
    const caixa = raiz.querySelector<HTMLElement>('#av-whatsapp');
    if (!caixa) return;
    interface EstadoWa {
      conta: { numero: string; sandbox: boolean } | null;
      sandbox: {
        numeroSandbox: string | null;
        sessao: { telefone: string; status: 'pending' | 'active'; expiraEm: string } | null;
      };
    }
    let e: EstadoWa;
    try {
      e = await api.get<EstadoWa>('/api/avisos/whatsapp');
    } catch (erro) {
      caixa.innerHTML = `<h3>💬 WhatsApp <span class="sub-inline">teste via Zernio</span></h3>
        <p class="aviso">${escapar(erro instanceof ErroApi ? erro.message : 'Não consegui consultar o Zernio.')}</p>`;
      return;
    }
    const sessao = e.sandbox.sessao;
    const estadoTexto = !sessao
      ? '<p class="dica">Nenhum aparelho ativado ainda. Digite seu WhatsApp (com DDD) e toque em Ativar — vai chegar uma mensagem do número de teste; <b>responda qualquer coisa</b> para ativar.</p>'
      : sessao.status === 'pending'
        ? `<p class="dica">⏳ Ativação enviada para <b>+${escapar(sessao.telefone)}</b> — abra o WhatsApp e <b>responda a mensagem</b> para ativar (vale até ${new Date(sessao.expiraEm).toLocaleString('pt-BR')}).</p>`
        : `<p class="dica av-ok">✅ Sessão ativa com <b>+${escapar(sessao.telefone)}</b> até ${new Date(sessao.expiraEm).toLocaleString('pt-BR')} — o teste de envio está liberado.</p>`;

    caixa.innerHTML = `
      <h3>💬 WhatsApp <span class="sub-inline">${e.conta?.sandbox ? `número de teste ${escapar(e.conta.numero)}` : `número próprio ${escapar(e.conta?.numero ?? '—')}`}</span></h3>
      ${e.conta?.sandbox ? '<p class="dica">O número próprio (62 3602-8654) está na análise regulatória do Zernio/Anatel — enquanto isso, o teste usa o número compartilhado deles.</p>' : ''}
      ${estadoTexto}
      <div class="form-2col">
        <label class="rotulado"><span>Seu WhatsApp (com DDD)</span>
          <input id="wa-fone" inputmode="tel" placeholder="62 99999-0000" value="${escapar(sessao ? `+${sessao.telefone}` : '')}"></label>
        <label class="rotulado"><span>Mensagem de teste</span>
          <input id="wa-texto" maxlength="1000" value="Teste do Sistema CD — o WhatsApp está funcionando. ✅"></label>
      </div>
      <div class="conf-acoes">
        <button class="btn btn-claro" id="wa-ativar">📲 Ativar este número</button>
        <button class="btn" id="wa-enviar" ${sessao?.status === 'active' ? '' : 'disabled'}>💬 Enviar teste</button>
        <button class="btn btn-claro" id="wa-atualizar">↻ Atualizar estado</button>
      </div>`;

    const fone = (): string => caixa.querySelector<HTMLInputElement>('#wa-fone')!.value.trim();
    caixa.querySelector('#wa-atualizar')!.addEventListener('click', () => void desenharWhatsApp());
    caixa.querySelector('#wa-ativar')!.addEventListener('click', () => {
      if (!fone()) return avisarBloqueante('Digite o número com DDD.');
      void api
        .post('/api/avisos/whatsapp/ativar', { telefone: fone() })
        .then(() => {
          avisar('Ativação enviada — responda a mensagem no seu WhatsApp.');
          return desenharWhatsApp();
        })
        .catch((er) => avisarBloqueante(er instanceof ErroApi ? er.message : 'Não consegui ativar.'));
    });
    caixa.querySelector('#wa-enviar')!.addEventListener('click', () => {
      const texto = caixa.querySelector<HTMLInputElement>('#wa-texto')!.value.trim();
      if (!fone()) return avisarBloqueante('Digite o número com DDD.');
      void api
        .post<{ de: string }>('/api/avisos/whatsapp/teste', { telefone: fone(), texto })
        .then((r) => avisar(`Mensagem enviada de ${r.de} — confira o WhatsApp.`))
        .catch((er) => avisarBloqueante(er instanceof ErroApi ? er.message : 'Não consegui enviar.'));
    });
  }

  async function salvar(chave: string, corpo: object, aoDarCerto?: () => void): Promise<void> {
    try {
      await api.put(`/api/avisos/${encodeURIComponent(chave)}`, corpo);
      aoDarCerto?.();
      avisar('Salvo.');
    } catch (e) {
      avisarBloqueante(e instanceof ErroApi ? e.message : 'Não consegui salvar.');
      void carregar();
    }
  }

  void carregar();
}
